/**
 * `<policy-editor-subjectbinding>` — the `SubjectBinding` body, both modes, plus the
 * API-key lifecycle.
 *
 * Implements `SC-F520` (JWT mode), `SC-F521` (API-key mode and key minting) and
 * `SC-F522` (per-subject rate limit and budget); stories `SC-S518`, `SC-S519`.
 *
 * **This form is a mode switch, and that is not a UI preference.** The two auth paths
 * read entirely different fields, and Sluice's own documentation states the asymmetry
 * backwards. On the JWT path `buildUserCtx` consumes only `claims.subjectId`,
 * `claims.email` and `claims.groups` and clones the raw claim map wholesale — so
 * `claims.tenantId` is read by nothing and a template must reach the underlying claim
 * as `{{ subject.claims.<path> }}`. On the API-key path the opposite holds:
 * `tenantId` and `allowedRegions` are stamped onto the subject as **literal values**
 * under those exact names, so `{{ subject.tenantId }}` resolves. One form for both
 * would make every template a coin flip.
 *
 * **The claim vocabulary is closed at exactly five keys.** Upstream declares an inline
 * extras map for application-specific mappings; it is tagged `json:"-"` with no custom
 * unmarshaler, and decoding goes YAML → JSON → `encoding/json`, so the tag wins and the
 * map is **never populated, in either mode**. There is therefore no "add a custom claim"
 * control, and the form says why rather than leaving a reader to conclude the product
 * forgot it (`SLUICE-U23`).
 *
 * **Key material is shown exactly once.** The control plane generates it, computes
 * `HMAC-SHA256(pepper, id + ":" + material)`, materialises only the hash as a
 * `secret://file` target and writes that target into `apiKeys[].hashRef`. The plaintext
 * is never stored, never logged and never returned again, so the reveal is deliberately
 * the hardest thing in this form to dismiss by accident.
 *
 * A note on what is *not* here: this build has no per-binding algorithm allowlist. The
 * object carries no such field, so the form does not invent one — it states what the
 * build accepts instead. (`SC-S518`'s algorithm-select criterion has nothing to bind
 * to until such a field exists upstream.)
 */

import { LitElement, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../../components/index.js";
import { toast, type BnSelectChangeDetail, type BnSelectOption } from "../../components/index.js";
import type { PolicyFinding } from "../../api/policies.js";
import { mintSubjectApiKey, revokeSubjectApiKey } from "../../api/policies.js";
import { ApiError, parseError } from "../../api/errors.js";

import { CLAIM_KEYS } from "./bindings.js";
import type { SpecChangeDetail } from "./editor-sqlaccess.js";
import type { PolicySchema, SchemaField } from "./schema.js";
import "./policy-pattern-list.js";
import type { PatternListChangeDetail } from "./policy-pattern-list.js";
import {
  asNumber,
  asStrings,
  asText,
  dropAt,
  fieldDiag,
  humanize,
  kindFormStyles,
  localDiag,
  putAt,
  renderBlockAlert,
  renderConstraint,
  renderDeadField,
  type DurationChangeDetail,
  type SecretRefChangeDetail,
} from "./kind-widgets.js";
import "./kind-widgets.js";

/** APIKeyBinding maps one key id to an identity. Nothing here is a credential: the
 * hash reference points at a materialised secret and the material never returns. */
export interface APIKeyBinding {
  id?: string;
  hashRef?: string;
  groups?: string[];
}

/** ClaimPaths is the closed five. */
export interface ClaimPaths {
  subjectId?: string;
  email?: string;
  groups?: string;
  tenantId?: string;
  allowedRegions?: string;
}

/** SubjectBindingSpec is `SubjectBinding.spec`. */
export interface SubjectBindingSpec {
  issuer?: string;
  audience?: string;
  jwksUrl?: string;
  hmacSecretRef?: string;
  jwksCacheTtl?: string;
  clockSkew?: string;
  claims?: ClaimPaths;
  /** Carried by an import and read by nothing. */
  groupsSource?: string;
  rateLimit?: { rps?: number; burst?: number };
  budget?: { cpuSecondsPerDay?: number; rowsPerDay?: number };
  apiKeys?: APIKeyBinding[];
}

/** Which credential this binding accepts. `both` exists only because an imported
 * object can populate both halves; the editor never creates one from scratch. */
type BindingMode = "jwt" | "apikey" | "both";

/** The three claim keys the JWT path actually reads, taken from the shared list so
 * the vocabulary is stated in one place. */
const JWT_CLAIM_KEYS: readonly string[] = CLAIM_KEYS.slice(0, 3);

/** The two whose meaning is mode-dependent. */
const LITERAL_ONLY_KEYS: readonly string[] = CLAIM_KEYS.slice(3);

/** The JWT fields, in reading order. */
const JWT_FIELDS = [
  "issuer",
  "audience",
  "jwksUrl",
  "hmacSecretRef",
  "jwksCacheTtl",
  "clockSkew",
] as const;

/** Expiry choices for a minted key. `never` is a sentinel word and not the empty
 * string, because bn-select reads `""` as "nothing selected" and would show its
 * placeholder in place of the label. */
const EXPIRY_OPTIONS: BnSelectOption[] = [
  { value: "2592000", label: "30 days" },
  { value: "7776000", label: "90 days" },
  { value: "31536000", label: "1 year" },
  {
    value: "never",
    label: "Does not expire",
    description: "Rotate it on a schedule of your own; nothing here will remind you.",
  },
];

/** A JSONPath-lite path: an optional leading `$`, dot segments, bracket array
 * indexing. It is **not** the grammar a claim check uses — that one is a plain dot
 * split with no `$` and no indexing — which is why the two never share a widget. */
const JSONPATH_LITE = /^\$?\.?[A-Za-z_][\w-]*(?:\[\d+\])*(?:\.[A-Za-z_][\w-]*(?:\[\d+\])*)*$/;

@customElement("policy-editor-subjectbinding")
export class PolicyEditorSubjectBinding extends LitElement {
  @property({ attribute: false }) schema!: PolicySchema;
  @property({ attribute: false }) spec: SubjectBindingSpec = {};
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  @property() instanceId = "";
  /** The object's stored name. Minting and revoking address the binding by it. */
  @property() objectName = "";
  /** False on a create flow: a key is minted against a stored binding. */
  @property({ type: Boolean }) saved = false;

  /** The author's mode choice, overriding what the spec implies. */
  @state() private chosenMode: BindingMode | "" = "";

  /** The pasted token for the claim probe. Decoded in the browser, never sent. */
  @state() private probe = "";

  @state() private minting = false;
  @state() private mintId = "";
  @state() private mintExpiry = "7776000";
  @state() private mintBusy = false;
  /** The server's refusal, rendered with its code and request id. */
  @state() private mintError: ApiError | null = null;
  /** A client-side refusal. Separate from `mintError` because `ApiError`'s
   * constructor maps a catalogue code onto the product's own copy, which would
   * replace a specific sentence with a generic one. */
  @state() private mintLocal = "";

  /**
   * The one-time key material. It lives in this field and nowhere else: not in a
   * store, not in the URL, not in a toast. Clearing it is irreversible from the UI's
   * side because it is irreversible from the server's.
   */
  @state() private revealed: { id: string; secret: string } | null = null;

  @state() private revoking = "";

  static override styles = [kindFormStyles];

  /* ── Mode ──────────────────────────────────────────────────────────── */

  private get impliedMode(): BindingMode {
    const jwt =
      asText(this.spec.issuer) !== "" ||
      asText(this.spec.jwksUrl) !== "" ||
      asText(this.spec.hmacSecretRef) !== "" ||
      asText(this.spec.audience) !== "";
    const keys = this.apiKeys.length > 0;
    if (jwt && keys) return "both";
    if (keys) return "apikey";
    return "jwt";
  }

  private get mode(): BindingMode {
    return this.chosenMode === "" ? this.impliedMode : this.chosenMode;
  }

  private get showJwt(): boolean {
    return this.mode === "jwt" || this.mode === "both";
  }

  private get showKeys(): boolean {
    return this.mode === "apikey" || this.mode === "both";
  }

  override willUpdate(changed: PropertyValues<this>) {
    // A mode the author has not chosen follows the object. Once they choose, the
    // choice sticks — until a different object is loaded.
    if (changed.has("objectName")) this.chosenMode = "";
  }

  /* ── Reading and writing ───────────────────────────────────────────── */

  private get claims(): ClaimPaths {
    return this.spec.claims ?? {};
  }

  private get apiKeys(): APIKeyBinding[] {
    return this.spec.apiKeys ?? [];
  }

  private emit(patch: Partial<SubjectBindingSpec>) {
    const next: SubjectBindingSpec = { ...this.spec, ...patch };
    for (const k of Object.keys(patch) as Array<keyof SubjectBindingSpec>) {
      if (patch[k] === undefined) delete next[k];
    }
    this.dispatchEvent(
      new CustomEvent<SpecChangeDetail<SubjectBindingSpec>>("spec-change", {
        detail: { spec: next },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Write one claim path. Clearing it deletes the key, and clearing the last one
   * removes `claims` — the renderer omits an absent field, so an empty object would
   * change the rendered bytes and therefore the digest for no authored change. */
  private setClaim(name: string, value: string) {
    const next: Record<string, unknown> = { ...this.claims };
    if (value === "") delete next[name];
    else next[name] = value;
    this.emit({ claims: Object.keys(next).length === 0 ? undefined : (next as ClaimPaths) });
  }

  private setNumber(group: "rateLimit" | "budget", name: string, raw: string) {
    const current: Record<string, unknown> = { ...(this.spec[group] ?? {}) };
    if (raw === "") delete current[name];
    else current[name] = asNumber(raw) ?? raw;
    const patch = {
      [group]: Object.keys(current).length === 0 ? undefined : current,
    } as Partial<SubjectBindingSpec>;
    this.emit(patch);
  }

  private writeKeys(keys: APIKeyBinding[]) {
    this.emit({ apiKeys: keys.length === 0 ? undefined : keys });
  }

  /* ── JWT half ──────────────────────────────────────────────────────── */

  private renderJwtField(name: string): TemplateResult | typeof nothing {
    const field = this.schema.field("subjectBindingSpec", name);
    if (!field) return nothing;
    const value = (this.spec as Record<string, unknown>)[name];
    const d = fieldDiag(this.findings, `spec.${name}`);
    const required = this.schema.required(field, this.spec as Record<string, unknown>);
    const label = humanize(name);

    if (field.widget === "secret-picker") {
      return html`<policy-secret-ref
        .instanceId=${this.instanceId}
        .value=${asText(value)}
        label=${label}
        help=${field.help ?? ""}
        ?required=${required}
        diagnostic=${d.error}
        rule=${d.rule}
        severity=${d.severity}
        @secret-change=${(e: CustomEvent<SecretRefChangeDetail>) => {
          e.stopPropagation();
          this.emit({ [name]: e.detail.value === "" ? undefined : e.detail.value });
        }}
      ></policy-secret-ref>`;
    }
    if (field.widget === "duration") {
      return html`<policy-duration
        label=${label}
        help=${field.help ?? ""}
        .value=${asText(value) || String(field.default ?? "")}
        diagnostic=${d.error}
        rule=${d.rule}
        severity=${d.severity}
        @duration-change=${(e: CustomEvent<DurationChangeDetail>) => {
          e.stopPropagation();
          this.emit({ [name]: e.detail.value === "" ? undefined : e.detail.value });
        }}
      ></policy-duration>`;
    }
    return html`<bn-field
      label=${label}
      hint=${field.help ?? ""}
      ?required=${required}
      error=${d.error}
      rule=${d.rule}
      severity=${d.severity}
    >
      <bn-input
        mono
        .value=${asText(value)}
        placeholder=${field.format === "url" ? "https://issuer.example/realms/acme" : ""}
        @input=${(e: CustomEvent<{ value: string }>) => {
          e.stopPropagation();
          this.emit({ [name]: e.detail.value === "" ? undefined : e.detail.value });
        }}
      ></bn-input>
    </bn-field>`;
  }

  /**
   * A claim mapping row.
   *
   * Whether the row is offered comes from the **mode**, which the schema cannot
   * express because it is a property of the running code path rather than of the
   * field. The label and the help still come from the schema.
   */
  private renderClaimField(name: string, literal: boolean): TemplateResult | typeof nothing {
    const field = this.schema.field("claimPaths", name);
    if (!field) return nothing;
    const value = asText((this.claims as Record<string, unknown>)[name]);
    const invalid = !literal && value !== "" && !JSONPATH_LITE.test(value);
    const d = localDiag(
      this.findings,
      `spec.claims.${name}`,
      invalid
        ? "Not a valid path. The grammar is an optional leading $, dot-separated segments and bracket array indexing — for example $.realm_access.roles or groups[0]."
        : "",
    );
    return html`<bn-field
      label=${humanize(name)}
      hint=${field.help ?? ""}
      error=${d.error}
      rule=${d.rule}
      severity=${d.severity}
    >
      <bn-input
        mono
        .value=${value}
        placeholder=${literal ? "acme" : String(field.default ?? "$.claim.path")}
        @input=${(e: CustomEvent<{ value: string }>) => {
          e.stopPropagation();
          this.setClaim(name, e.detail.value);
        }}
      ></bn-input>
    </bn-field>`;
  }

  /**
   * The claim probe of `03` §3.10: paste a token, see what each mapping extracts.
   *
   * It is decoded **in the browser**, never sent to the server and never stored. The
   * signature is not verified and the form says so: the probe answers "does my path
   * find the value", which is the question, and verifying would need key material this
   * screen deliberately does not have.
   */
  private renderProbe() {
    const decoded = decodeJwtPayload(this.probe);
    return html`<bn-panel eyebrow="Claim probe">
      <bn-field
        label="Paste a token from this issuer"
        hint="Decoded in your browser. It is never sent to Sluice Cloud, never stored, and its signature is not checked — the probe answers whether your paths find the values."
      >
        <bn-input
          mono
          .value=${this.probe}
          placeholder="eyJhbGciOi…"
          @input=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.probe = e.detail.value;
          }}
        ></bn-input>
      </bn-field>
      ${this.probe.trim() === ""
        ? nothing
        : decoded === null
          ? html`<p class="sub" role="status">
              That does not decode as a JWT payload. A JWT is three base64url segments separated by
              dots.
            </p>`
          : html`<bn-kv
              .rows=${JWT_CLAIM_KEYS.map((k) => {
                const path =
                  asText((this.claims as Record<string, unknown>)[k]) ||
                  String(this.schema.field("claimPaths", k)?.default ?? "");
                const found = path === "" ? undefined : extractPath(decoded, path);
                return {
                  label: `${humanize(k)}${path === "" ? "" : ` · ${path}`}`,
                  ...(found === undefined ? {} : { value: renderClaim(found) }),
                };
              })}
              unsetText="no path set, or the path found nothing"
            ></bn-kv>`}
    </bn-panel>`;
  }

  private renderJwt() {
    return html`
      <section>
        <h3>JWT from an issuer</h3>
        <div class="row">${JWT_FIELDS.map((f) => this.renderJwtField(f))}</div>
        ${renderConstraint(
          this.schema,
          "SubjectBinding",
          "binding.jwks-needs-issuer-and-audience",
          "What a JWKS URL requires",
        )}
        ${renderConstraint(
          this.schema,
          "SubjectBinding",
          "binding.issuer-unique",
          "Issuers must be unique across bindings",
        )}
      </section>

      <section>
        <h3>Claim mapping</h3>
        <p class="lede">
          These are the three paths the JWT path actually reads. Every other claim on the token is
          still available to policies as <code>{{ subject.claims.&lt;path&gt; }}</code> — the raw claim
          map is cloned onto the subject wholesale — so a claim you do not map here is not a claim you
          have lost.
        </p>
        <div class="row">${JWT_CLAIM_KEYS.map((k) => this.renderClaimField(k, false))}</div>
        ${this.renderModeAsymmetry()}
      </section>

      <section>${this.renderProbe()}</section>

      <section>
        <bn-panel eyebrow="How the key set is fetched, and what is accepted">
          <p class="sub">
            JWKS is fetched with a 5-second timeout and cached for 10 minutes. An unknown key id
            triggers a refresh that is coalesced and rate-limited to one per 30 seconds, and a fetch
            failure serves the <strong>stale</strong> key set rather than failing closed.
          </p>
          <p class="sub">
            This build has no per-binding algorithm allowlist — the object carries no field for one, so
            this form does not offer a control that would bind to nothing. A shared secret backs HS256
            and HS384; a JWKS key set backs the asymmetric algorithms it publishes. <code>none</code> is
            never accepted.
          </p>
        </bn-panel>
      </section>
    `;
  }

  /** The two claim keys that are literals in API-key mode and read by nothing in JWT
   * mode. Not offered here — and when an imported object carries one, shown disabled
   * with the reason, because the value is there and does nothing. */
  private renderModeAsymmetry() {
    const carried = LITERAL_ONLY_KEYS.filter(
      (k) => asText((this.claims as Record<string, unknown>)[k]) !== "",
    );
    const kindSchema = this.schema.kind("SubjectBinding");
    const statement =
      kindSchema?.constraints?.find((c) => c.id === "binding.jwt-claim-paths")?.statement ?? "";
    if (carried.length === 0) {
      return statement === ""
        ? nothing
        : html`<p class="sub">
            <code>tenantId</code> and <code>allowedRegions</code> are not offered in this mode.
            ${statement}
          </p>`;
    }
    return html`<bn-panel eyebrow="Carried, and read by nothing in this mode">
      ${carried.map((k) => {
        const field = this.schema.field("claimPaths", k);
        return html`<bn-field label=${humanize(k)}>
          <div class="dead">
            <bn-input mono disabled .value=${asText((this.claims as Record<string, unknown>)[k])}></bn-input>
            <div class="dead-reason">
              <bn-badge variant="not-enforced">not read on the JWT path</bn-badge>
              ${statement}${field?.source ? html` <code>${field.source}</code>` : nothing}
            </div>
          </div>
        </bn-field>`;
      })}
      <div class="row tight">
        <bn-button
          size="sm"
          @click=${() => {
            const next: Record<string, unknown> = { ...this.claims };
            for (const k of carried) delete next[k];
            this.emit({
              claims: Object.keys(next).length === 0 ? undefined : (next as ClaimPaths),
            });
          }}
        >
          Remove ${carried.length === 1 ? "it" : "them"}
        </bn-button>
        <span class="sub">
          Removing has no runtime effect in this mode; it only stops the manifest from implying one.
        </span>
      </div>
    </bn-panel>`;
  }

  /* ── API-key half ──────────────────────────────────────────────────── */

  private renderKeyClaims() {
    const fields = this.schema.fields("claimPaths");
    return html`<section>
      <h3>Literal claims</h3>
      <p class="lede">${this.schema.groupHelp("claimPaths")}</p>
      <div class="row">${fields.map((f: SchemaField) => this.renderClaimField(f.name, true))}</div>
      <bn-panel eyebrow="There are no custom claims in this build">
        <p>
          The vocabulary above is the whole of it — five keys. Sluice's grammar declares an inline map
          for application-specific claims, and the field is tagged so the JSON decoder never populates
          it; decoding goes YAML to JSON to <code>encoding/json</code>, so an extra key under
          <code>claims</code> is <strong>silently dropped</strong> in either mode.
        </p>
        <p class="sub">
          Offering an "add custom claim" control would produce a manifest that reads correctly and binds
          nothing. Upstream fix: <code>SLUICE-U23</code>.
        </p>
      </bn-panel>
      <p class="sub">
        <code>groups</code> carries an overload worth knowing: in this mode it is used as a
        <strong>single literal group</strong> when a key's own groups list is empty.
      </p>
    </section>`;
  }

  private renderKeyRow(key: APIKeyBinding, index: number): TemplateResult {
    const id = asText(key.id);
    const hashRef = asText(key.hashRef);
    const dotted = id.includes(".");
    const idDiag = localDiag(
      this.findings,
      `spec.apiKeys[${index}].id`,
      dotted
        ? "Sluice splits a presented key on the LAST dot, so an id containing one still parses and reads confusingly. Use a dot-free id."
        : "",
      "warning",
    );
    const hashDiag = fieldDiag(this.findings, `spec.apiKeys[${index}].hashRef`);

    return html`<li class="item">
      <div class="item-head">
        <span class="item-title">${id === "" ? `Key ${index + 1}` : id}</span>
        <span class="spacer"></span>
        <bn-button
          size="sm"
          variant="danger"
          ?loading=${this.revoking === id}
          aria-label=${`Revoke ${id || `key ${index + 1}`}`}
          @click=${() => void this.revoke(id, index)}
        >
          Revoke
        </bn-button>
      </div>
      <div class="row">
        <bn-field
          label="Key id"
          hint=${this.schema.help("apiKeyBinding", "id")}
          required
          error=${idDiag.error}
          rule=${idDiag.rule}
          severity=${idDiag.severity}
        >
          <bn-input
            mono
            .value=${id}
            placeholder="etl-nightly"
            @input=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.writeKeys(putAt(this.apiKeys, index, { ...key, id: e.detail.value }));
            }}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Hash reference"
          hint=${this.schema.help("apiKeyBinding", "hashRef")}
          error=${hashDiag.error}
          rule=${hashDiag.rule}
          severity=${hashDiag.severity}
        >
          <bn-input
            mono
            disabled
            .value=${hashRef}
            placeholder="generated when the key is minted"
          ></bn-input>
        </bn-field>
      </div>
      <policy-pattern-list
        label="Groups this key's subject belongs to"
        scope="columns"
        no-preview
        path=${`spec.apiKeys[${index}].groups`}
        .values=${asStrings(key.groups)}
        .help=${this.schema.help("apiKeyBinding", "groups")}
        .findings=${this.findings}
        @patterns-change=${(e: CustomEvent<PatternListChangeDetail>) => {
          e.stopPropagation();
          const next: APIKeyBinding = { ...key };
          if (e.detail.values.length === 0) delete next.groups;
          else next.groups = e.detail.values;
          this.writeKeys(putAt(this.apiKeys, index, next));
        }}
      ></policy-pattern-list>
      ${hashRef === ""
        ? html`<p class="sub">
            This key has no materialised hash, so Sluice <strong>skips it with a warning</strong>
            rather than failing the binding — one integration stops authenticating and nothing looks
            broken. Mint the key to generate the reference.
          </p>`
        : nothing}
    </li>`;
  }

  private renderKeys() {
    const keys = this.apiKeys;
    return html`
      <section>
        <h3>API keys</h3>
        <p class="lede">${this.schema.groupHelp("apiKeyBinding")}</p>
        ${keys.length === 0
          ? html`<bn-empty-state
              icon="⚿"
              heading="No keys on this binding"
              message="A binding in API-key mode with no keys authenticates nobody. Minting one generates the material, shows it once, and stores only its HMAC."
            ></bn-empty-state>`
          : html`<ul class="items">
              ${keys.map((k, i) => this.renderKeyRow(k, i))}
            </ul>`}
        <div class="row tight">
          <bn-button variant="primary" ?disabled=${!this.canMint} @click=${this.openMint}>
            Mint a key
          </bn-button>
          ${this.canMint
            ? nothing
            : html`<span class="sub">
                Save the binding first — a key is minted against a stored binding, and its hash is
                materialised into that binding's secret namespace.
              </span>`}
        </div>
        ${renderBlockAlert(this.findings, "spec.apiKeys")}
        ${renderConstraint(
          this.schema,
          "SubjectBinding",
          "binding.secrets-exist",
          "Before this can be published",
        )}
      </section>
      ${this.renderMintDialog()} ${this.renderRevealDialog()}
    `;
  }

  private get canMint(): boolean {
    return this.saved && this.objectName !== "" && this.instanceId !== "";
  }

  private openMint() {
    this.mintId = "";
    this.mintExpiry = "7776000";
    this.mintError = null;
    this.mintLocal = "";
    this.minting = true;
  }

  /**
   * Mint one key.
   *
   * The control plane generates the material and computes the HMAC itself — there is
   * no shelling out to `sluice apikey hash` — and it writes the binding's `hashRef` as
   * part of the same operation. So the object this form is showing is stale afterwards,
   * and the form asks its host to refetch rather than patching a row in locally: a
   * locally invented `apiKeys` entry would have no `hashRef` and would look exactly like
   * the silent-partial-outage state.
   */
  private async mint() {
    const id = this.mintId.trim();
    if (id === "") {
      this.mintLocal = "Name the key first — the id is also the subject id Sluice will see.";
      return;
    }
    if (id.includes(".")) {
      this.mintLocal =
        "Use an id without a dot: Sluice splits a presented key on the last dot, so a dotted id parses but reads confusingly.";
      return;
    }
    this.mintBusy = true;
    this.mintError = null;
    this.mintLocal = "";
    try {
      const created = await mintSubjectApiKey(this.instanceId, this.objectName, {
        expires_in_seconds: this.mintExpiry === "never" ? null : Number(this.mintExpiry),
      });
      this.minting = false;
      this.revealed = { id: created.id || id, secret: created.secret };
      this.requestRefetch();
    } catch (err) {
      this.mintError = parseError(err);
    } finally {
      this.mintBusy = false;
    }
  }

  private async revoke(id: string, index: number) {
    if (id === "" || !this.canMint) {
      // An unsaved row has no server-side counterpart, so dropping it locally is the
      // whole of the operation.
      this.writeKeys(dropAt(this.apiKeys, index));
      return;
    }
    this.revoking = id;
    try {
      await revokeSubjectApiKey(this.instanceId, this.objectName, id);
      toast(`Key ${id} revoked. It stops working once the next publish is confirmed.`, "success");
      this.requestRefetch();
    } catch (err) {
      const e = parseError(err);
      toast(`${e.message}${e.requestId ? ` (reference ${e.requestId})` : ""}`, "error");
    } finally {
      this.revoking = "";
    }
  }

  /** Ask the host to refetch the object. Composed, so the routed page and the generic
   * editor shell can both pick it up. */
  private requestRefetch() {
    this.dispatchEvent(new CustomEvent("policy-object-stale", { bubbles: true, composed: true }));
  }

  private renderMintDialog() {
    const err = this.mintError;
    return html`<bn-modal
      ?open=${this.minting}
      heading="Mint an API key"
      .width=${540}
      @close=${() => (this.minting = false)}
    >
      <p>
        Sluice Cloud generates the material, computes
        <code>HMAC-SHA256(pepper, id + ":" + material)</code>, and stores only that hash. The full key
        is shown to you <strong>once</strong>.
      </p>
      <bn-field
        label="Key id"
        hint="Also the subject id, unless the binding sets a literal subjectId claim. Avoid dots: Sluice splits a presented key on the last one."
        required
        error=${this.mintLocal}
      >
        <bn-input
          mono
          .value=${this.mintId}
          ?invalid=${this.mintLocal !== ""}
          placeholder="etl-nightly"
          @input=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.mintId = e.detail.value;
            this.mintLocal = "";
          }}
        ></bn-input>
      </bn-field>
      <bn-field label="Expires">
        <bn-select
          .options=${EXPIRY_OPTIONS}
          .value=${this.mintExpiry}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            e.stopPropagation();
            this.mintExpiry = e.detail.value;
          }}
        ></bn-select>
      </bn-field>
      ${err
        ? html`<div role="alert">
            <p>${err.message}</p>
            <bn-kv
              .rows=${[
                { label: "Error code", value: err.code },
                ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
              ]}
            ></bn-kv>
          </div>`
        : nothing}
      <p slot="consequence">
        The key material is shown once and cannot be recovered. Copy it before closing the next dialog
        — Sluice Cloud stores only its HMAC and has nothing to show you again.
      </p>
      <bn-button slot="footer" @click=${() => (this.minting = false)}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.mintBusy}
        @click=${() => void this.mint()}
      >
        Mint the key
      </bn-button>
    </bn-modal>`;
  }

  /**
   * The once-only reveal.
   *
   * The value is a copyable bn-kv row rather than selectable text, so copying is one
   * keyboard-reachable action. The dialog carries the destructive tone and a
   * consequence block because closing it destroys the only copy that exists.
   */
  private renderRevealDialog() {
    const revealed = this.revealed;
    if (!revealed) return nothing;
    return html`<bn-modal
      open
      tone="destructive"
      heading="Copy this key now"
      .width=${640}
      @close=${() => (this.revealed = null)}
    >
      <p>
        This is the complete key: the id and the material joined by a dot. Sluice authenticates it by
        splitting on the last dot and verifying the material against the stored HMAC.
      </p>
      <bn-kv
        .rows=${[
          { label: "Key id", value: revealed.id },
          { label: "Full key", value: `${revealed.id}.${revealed.secret}` },
        ]}
      ></bn-kv>
      <p class="sub">
        Revoking is not instantaneous either: a revocation reaches the instance on the reload path, and
        the key stops working only once the config digest read-back confirms the new generation.
      </p>
      <p slot="consequence">
        Closing this dialog destroys the only copy Sluice Cloud will ever have. There is no "show it
        again".
      </p>
      <bn-button slot="footer" variant="danger" @click=${() => (this.revealed = null)}>
        I have copied it — close
      </bn-button>
    </bn-modal>`;
  }

  /* ── Limits (SC-F522) ──────────────────────────────────────────────── */

  private renderNumberGroup(name: "rateLimit" | "budget", heading: string) {
    const group = this.schema.field("subjectBindingSpec", name)?.group ?? name;
    const fields = this.schema.fields(group);
    if (fields.length === 0) return nothing;
    const held = (this.spec[name] ?? {}) as Record<string, unknown>;
    return html`<section>
      <h3>${heading}</h3>
      <p class="lede">${this.schema.groupHelp(group)}</p>
      <div class="row">
        ${fields.map((f: SchemaField) => {
          const d = fieldDiag(this.findings, `spec.${name}.${f.name}`);
          const value = asNumber(held[f.name]);
          return html`<bn-field
            label=${humanize(f.name)}
            hint=${f.help ?? ""}
            error=${d.error}
            rule=${d.rule}
            severity=${d.severity}
          >
            <bn-input
              type="number"
              inputmode="decimal"
              .value=${value === undefined ? "" : String(value)}
              placeholder=${String(f.default ?? "")}
              @input=${(e: CustomEvent<{ value: string }>) => {
                e.stopPropagation();
                this.setNumber(name, f.name, e.detail.value);
              }}
            ></bn-input>
          </bn-field>`;
        })}
      </div>
    </section>`;
  }

  /** Fields the schema marks hidden that an import nonetheless populated —
   * `groupsSource` is the one. They decode, they travel into the bundle, and no code
   * path in this build reads them, so they are shown rather than dropped. */
  private renderInertFields() {
    const rows = this.schema
      .fields("subjectBindingSpec")
      .filter(
        (f: SchemaField) =>
          this.schema.offer(f) === "hidden" &&
          (this.spec as Record<string, unknown>)[f.name] !== undefined,
      )
      .map((f: SchemaField) =>
        renderDeadField(this.schema, f, asText((this.spec as Record<string, unknown>)[f.name])),
      );
    if (rows.length === 0) return nothing;
    return html`<section>
      <h3>Carried, and read by nothing</h3>
      <p class="lede">
        These arrived with an import. They decode, they travel into the bundle, and no code path in
        this build reads them — so they are shown rather than dropped, because removing a value
        silently is how a manifest stops matching what its author believes it says.
      </p>
      ${rows}
    </section>`;
  }

  override render() {
    if (!this.schema) return nothing;

    return html`
      <section>
        <bn-panel eyebrow="What this kind does">
          <p class="lede">${this.schema.kind("SubjectBinding")?.help ?? ""}</p>
        </bn-panel>
        <bn-field
          label="This binding authenticates"
          hint="The two paths read different fields, and Sluice's own documentation states the difference backwards. Choosing here decides which claim mapping is offered and which template paths will resolve."
        >
          <bn-select
            .options=${[
              {
                value: "jwt",
                label: "JWT from an issuer",
                description:
                  "Only subjectId, email and groups are read. Every other claim reaches policies as subject.claims.<path>.",
              },
              {
                value: "apikey",
                label: "API keys",
                description:
                  "All five claim keys are stamped onto the subject as literal values, so subject.tenantId resolves.",
              },
              {
                value: "both",
                label: "Both",
                description:
                  "Legal, and normally only produced by an import. Each credential still follows its own path.",
              },
            ]}
            .value=${this.mode}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              e.stopPropagation();
              this.chosenMode = e.detail.value as BindingMode;
            }}
          ></bn-select>
        </bn-field>
      </section>

      ${this.showJwt ? this.renderJwt() : nothing}
      ${this.showKeys ? html`${this.renderKeyClaims()} ${this.renderKeys()}` : nothing}
      ${this.renderNumberGroup("rateLimit", "Rate limit")}
      ${this.renderNumberGroup("budget", "Daily budget")} ${this.renderInertFields()}

      <section>
        <bn-panel eyebrow="A publish of this kind does not restart the instance">
          <p class="sub">
            <code>SubjectBinding</code> is one of the two kinds a publish can carry on the hot-reload
            path. That makes a change cheap and it does not make it instantaneous: it takes effect once
            the instance's config digest read-back confirms the new generation.
          </p>
        </bn-panel>
      </section>
    `;
  }
}

/**
 * decodeJwtPayload base64url-decodes the middle segment of a JWT, or null.
 *
 * It verifies nothing, and deliberately does not try: verification needs the issuer's
 * key material, which this screen does not have and should not have.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.trim().split(".");
  if (parts.length < 2) return null;
  const segment = parts[1] ?? "";
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
    // A payload with non-ASCII claim values needs the UTF-8 round trip atob alone
    // does not perform.
    const json = decodeURIComponent(
      Array.from(binary, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""),
    );
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** extractPath walks a JSONPath-lite path: optional `$`, dot segments, `[n]`. */
function extractPath(root: Record<string, unknown>, path: string): unknown {
  let cur: unknown = root;
  const cleaned = path.replace(/^\$\.?/, "");
  if (cleaned === "") return undefined;
  for (const raw of cleaned.split(".")) {
    const m = /^([A-Za-z_][\w-]*)((?:\[\d+\])*)$/.exec(raw);
    if (!m) return undefined;
    if (!cur || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[m[1] ?? ""];
    for (const idx of (m[2] ?? "").matchAll(/\[(\d+)\]/g)) {
      if (!Array.isArray(cur)) return undefined;
      cur = cur[Number(idx[1])];
    }
  }
  return cur;
}

/** renderClaim shows an extracted value without pretending a list is a string. */
function renderClaim(value: unknown): string {
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  if (value === null) return "null";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-editor-subjectbinding": PolicyEditorSubjectBinding;
  }
}
